/**
 * @file     gaEventParams.ts
 * @purpose  Builds GA4 event params that carry no free text or addresses and
 *           only scalar values. GA4 drops array/object params and stores every
 *           string verbatim, so typed queries, addresses and error messages are
 *           reduced to lengths, counts and enum-like joined strings here. The
 *           first-party reltaTrack payloads keep the raw values they need.
 */
import type { TraditionalSearchValue } from '@/components/search/searchValue';

/** GA4 truncates event param values past 100 characters. */
const GA_PARAM_MAX = 100;

export function joinForGa(values: readonly string[] | undefined): string {
  return (values ?? []).join(',').slice(0, GA_PARAM_MAX);
}

function textLength(value: string | undefined): number {
  return value?.trim().length ?? 0;
}

type SearchFilters = Pick<TraditionalSearchValue, 'address' | 'keywords' | 'regions' | 'pills'>;

export function searchGaParams(filters: SearchFilters): Record<string, string | number | boolean> {
  const addressLength = textLength(filters.address);
  return {
    has_address: addressLength > 0,
    address_length: addressLength,
    keyword_count: filters.keywords?.length ?? 0,
    regions: joinForGa(filters.regions),
    region_count: filters.regions?.length ?? 0,
    pills: joinForGa(filters.pills),
    pill_count: filters.pills?.length ?? 0,
  };
}

type ZeroResultFilters = SearchFilters &
  Pick<TraditionalSearchValue, 'propertyTypes' | 'priceMin' | 'priceMax' | 'bedsMin' | 'bathsMin' | 'statuses'>;

export function zeroResultsGaParams(
  filters: ZeroResultFilters,
): Record<string, string | number | boolean> {
  return {
    ...searchGaParams(filters),
    property_types: joinForGa(filters.propertyTypes),
    statuses: joinForGa(filters.statuses),
    ...(filters.priceMin != null ? { price_min: filters.priceMin } : {}),
    ...(filters.priceMax != null ? { price_max: filters.priceMax } : {}),
    ...(filters.bedsMin != null ? { beds_min: filters.bedsMin } : {}),
    ...(filters.bathsMin != null ? { baths_min: filters.bathsMin } : {}),
  };
}

export function freeTextGaParams(text: string): { query_length: number; query_word_count: number } {
  const trimmed = text.trim();
  return {
    query_length: trimmed.length,
    query_word_count: trimmed ? trimmed.split(/\s+/).length : 0,
  };
}

const ERROR_CLASS_RE = /\b([A-Z][A-Za-z0-9]*(?:Error|Exception))\b/;

/**
 * Error messages can embed user input, URLs and tokens, so GA only gets the
 * error class. window.onerror hands over "Uncaught TypeError: ..." with no
 * Error object, which is why the class is parsed rather than read from `name`.
 */
export function errorClassOf(error: { message?: string; stack?: string }): string {
  const firstStackLine = error.stack?.split('\n', 1)[0];
  return (
    (firstStackLine && ERROR_CLASS_RE.exec(firstStackLine)?.[1]) ||
    (error.message && ERROR_CLASS_RE.exec(error.message)?.[1]) ||
    'unknown'
  );
}
